/**
 * Trusted Types Configuration
 *
 * Implements Trusted Types to mitigate DOM-based XSS attacks by enforcing
 * that dangerous DOM sinks (like innerHTML) only accept typed objects.
 *
 * Browser Support:
 * - Chrome 83+ ✅
 * - Edge 83+ ✅
 * - Firefox ❌ (not yet supported)
 * - Safari ❌ (not yet supported)
 *
 * For unsupported browsers, this gracefully degrades to passthrough.
 */

// Check if Trusted Types API is available
const trustedTypesAvailable = typeof window.trustedTypes !== 'undefined';

/**
 * Default policy for sanitizing HTML content
 * This policy escapes HTML by default to prevent XSS
 */
let defaultPolicy = null;

/**
 * Tooltip policy for creating safe tooltip HTML
 * This policy allows specific safe HTML structures for tooltips
 */
let tooltipPolicy = null;

if (trustedTypesAvailable) {
  try {
    // Create default policy (fallback for any unhandled HTML)
    // This escapes all HTML to prevent XSS
    defaultPolicy = window.trustedTypes.createPolicy('default', {
      createHTML: (input) => {
        // Allow LiveView to set raw HTML when needed
        // LiveView already sanitizes on the server side
        if (typeof input === 'string') {
          // Check if this looks like LiveView DOM manipulation
          // LiveView uses specific patterns we can trust
          if (input.includes('phx-') || input.includes('data-phx-')) {
            return input;
          }
          // For other content, escape HTML to prevent XSS
          const div = document.createElement('div');
          div.textContent = input;
          return div.innerHTML;
        }
        return String(input);
      }
    });

    // Create tooltip-specific policy with controlled HTML
    // This allows specific safe HTML structures for price tooltips
    tooltipPolicy = window.trustedTypes.createPolicy('tooltip', {
      createHTML: (input) => {
        // Parse the input to ensure it matches our expected structure
        // This is a whitelist approach - only allow specific safe patterns

        if (typeof input !== 'string') {
          return '';
        }

        // For tooltip content, we control the structure completely
        // The input should be a JSON string with sanitized values
        try {
          const data = JSON.parse(input);

          // Validate and sanitize inputs
          const price = sanitizePrice(data.price);
          const date = sanitizeText(data.date);
          const type = data.type === 'forecast' ? 'forecast' : 'history';
          const typeLabel = type === 'forecast' ? ' (Forecast)' : '';
          const typeColor = type === 'forecast' ? 'text-emerald-400' : 'text-blue-400';
          const bgColor = type === 'forecast' ? 'bg-emerald-400' : 'bg-blue-500';

          // Return controlled HTML structure
          return `
            <div class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full ${bgColor} ring-2 ring-white/20"></div>
              <div class="font-bold text-lg tracking-tight">£${price}</div>
              ${typeLabel ? `<span class="text-xs font-medium ${typeColor} uppercase tracking-wider">${typeLabel.trim()}</span>` : ''}
            </div>
            <div class="text-xs text-gray-300 font-medium mt-1">${date}</div>
          `;
        } catch (e) {
          console.error('Invalid tooltip data:', e);
          return '';
        }
      }
    });
  } catch (e) {
    console.error('Failed to create Trusted Types policy:', e);
  }
}

/**
 * Sanitize price value - only allows numbers and decimal points
 */
function sanitizePrice(value) {
  if (typeof value !== 'string') {
    value = String(value);
  }
  // Remove any non-numeric characters except decimal point
  return value.replace(/[^0-9.]/g, '');
}

/**
 * Sanitize text content - escapes HTML entities
 */
function sanitizeText(value) {
  if (typeof value !== 'string') {
    value = String(value);
  }
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * Create safe HTML for tooltips
 * @param {Object} data - Tooltip data { price, date, type }
 * @returns {TrustedHTML|string} Safe HTML content
 */
export function createTooltipHTML(data) {
  if (trustedTypesAvailable && tooltipPolicy) {
    return tooltipPolicy.createHTML(JSON.stringify(data));
  }

  // Fallback for browsers without Trusted Types support
  // Still sanitize the content
  const price = sanitizePrice(data.price);
  const date = sanitizeText(data.date);
  const type = data.type === 'forecast' ? 'forecast' : 'history';
  const typeLabel = type === 'forecast' ? ' (Forecast)' : '';
  const typeColor = type === 'forecast' ? 'text-emerald-400' : 'text-blue-400';
  const bgColor = type === 'forecast' ? 'bg-emerald-400' : 'bg-blue-500';

  return `
    <div class="flex items-center gap-2">
      <div class="w-2 h-2 rounded-full ${bgColor} ring-2 ring-white/20"></div>
      <div class="font-bold text-lg tracking-tight">£${price}</div>
      ${typeLabel ? `<span class="text-xs font-medium ${typeColor} uppercase tracking-wider">${typeLabel.trim()}</span>` : ''}
    </div>
    <div class="text-xs text-gray-300 font-medium mt-1">${date}</div>
  `;
}

/**
 * Check if Trusted Types is supported
 */
export function isTrustedTypesSupported() {
  return trustedTypesAvailable;
}
